/**
 * Encode arbitrary data as a Base64 string for storage in directive attributes.
 */
export function encodeBlockData(data: unknown): string {
    const jsonString = JSON.stringify(data);
    return btoa(unescape(encodeURIComponent(jsonString)));
}

/**
 * Decode a Base64-encoded (or legacy HTML-entity-encoded) string back to a raw
 * JSON string.  The caller is responsible for JSON.parse().
 */
export function decodeBlockData(str: string): string {
    if (!str) return '';

    // If the string doesn't contain HTML entities or raw JSON markers,
    // assume it's Base64.
    if (!str.includes('&') && !str.includes('"') && !str.includes('{')) {
        try {
            return decodeURIComponent(escape(atob(str)));
        } catch (e) {
            console.warn(
                'Failed to decode as Base64, treating as plain string:',
                e
            );
            return str;
        }
    }

    // Legacy HTML-entity decoding for backward compatibility.
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
}

/**
 * Convenience: decode + JSON.parse in one call, with a fallback value.
 */
export function decodeBlockDataAs<T>(str: string | undefined, fallback: T): T {
    if (!str) return fallback;
    try {
        const json = decodeBlockData(str);
        return JSON.parse(json) as T;
    } catch (e) {
        console.warn('Failed to decode block data:', e);
        return fallback;
    }
}

/**
 * Returns true if str decodes and JSON-parses cleanly — i.e. {@link decodeBlockDataAs}
 * would return the real value rather than silently falling back to defaults.
 * Used to detect corrupted directive payloads before applying edited source.
 */
export function isDecodableBlockData(str: string | undefined): boolean {
    if (!str) return false;
    try {
        JSON.parse(decodeBlockData(str));
        return true;
    } catch {
        return false;
    }
}
